import { useEffect, useMemo, useState } from 'react';

import { ColumnData } from '@frontend/api';
import {
  Button,
  IconButton,
  Popover,
  PopoverBody,
  PopoverButtonGroup,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Tooltip,
  ColumnsIcon,
} from '@frontend/legacy-components';

import { VisibleColumnsConfig } from './VisibleColumnsConfig';
import { ColumnTransformer } from './transformations';
import type { ColumnState } from './types';

export type CustomizeColumnsPopoverProps = {
  columnData: ColumnData[];
  availableColumns: ColumnData[];
  displayedColumns: ColumnState[];
  onColumnsPopoverSubmit: (flattenedNewColumns: ColumnState[]) => void;
  /**
   * When true, the footer's secondary button is "Reset all" — re-checks every
   * column and restores the canonical default order implied by `columnData`
   * without closing the popover (the user still confirms via Apply). When
   * false/omitted, the secondary button is "Cancel" (closes the popover and
   * discards staged changes).
   */
  showResetAll?: boolean;
};

export const CustomizeColumnsPopover = ({
  columnData, // nested data of available columns
  availableColumns, // flattened array of available columns
  displayedColumns, // ordered array of currently displayed column state (flattened)
  onColumnsPopoverSubmit,
  showResetAll = false,
}: CustomizeColumnsPopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // NOTE: we are doing this to prevent re-renders while outer state updates due to various
  //  things. By using a stringified list, it prevents the effect from re-firing from
  //  state updates that do not change the order or selection state of viisble columns
  const stringifiedVisibleColumns = useMemo(() => {
    return displayedColumns.map((c) => c.queryParamName).join('|');
  }, [displayedColumns]);
  const defaultVisibleColumnsState = useMemo(
    () =>
      ColumnTransformer.transformDisplayQueryParamNamesToVisibleColumnConfigState(
        stringifiedVisibleColumns.split('|'),
        columnData,
      ),
    [columnData, stringifiedVisibleColumns],
  );
  const [visibleColumnsState, setVisibleColumnsState] = useState<ColumnState[]>(
    defaultVisibleColumnsState,
  );

  useEffect(() => {
    setVisibleColumnsState(defaultVisibleColumnsState);
  }, [defaultVisibleColumnsState]);

  const closePopover = () => {
    setIsPopoverOpen(false);
    setVisibleColumnsState(defaultVisibleColumnsState);
  };
  const openPopover = () => setIsPopoverOpen(true);
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedColumns =
      ColumnTransformer.transformNestedColumnStateToFlatColumnState(
        availableColumns,
        visibleColumnsState,
      );
    onColumnsPopoverSubmit(updatedColumns);
    closePopover();
  };

  const handleResetAll = () => {
    // Rebuild staged state as "all columns visible in canonical default order."
    // Passing every availableColumn's queryParamName causes the transformer to
    // mark every top-level column and child as visible and preserves the order
    // implied by `columnData`.
    setVisibleColumnsState(
      ColumnTransformer.transformDisplayQueryParamNamesToVisibleColumnConfigState(
        availableColumns.map((c) => c.queryParamName),
        columnData,
      ),
    );
  };

  return (
    <Popover
      size="sm"
      placement={'bottom-start'}
      isOpen={isPopoverOpen}
      onClose={closePopover}
    >
      <PopoverTrigger>
        <IconButton onClick={openPopover}>
          <Tooltip
            label="Customize Columns"
            aria-label="Customize Columns"
            placement="top"
          >
            <ColumnsIcon />
          </Tooltip>
        </IconButton>
      </PopoverTrigger>
      <Portal>
        <PopoverContent as="form" onSubmit={onSubmit}>
          <PopoverHeader>Customize Columns</PopoverHeader>
          <PopoverBody maxHeight="calc(100vh - 350px)" overflowY="auto">
            <VisibleColumnsConfig
              visibleColumnsState={visibleColumnsState}
              setVisibleColumnsState={setVisibleColumnsState}
            />
          </PopoverBody>
          <PopoverFooter>
            <PopoverButtonGroup>
              {showResetAll ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleResetAll}
                >
                  Reset all
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={closePopover}
                >
                  Cancel
                </Button>
              )}
              <Button variant="primary" type="submit">
                Apply
              </Button>
            </PopoverButtonGroup>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
